import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, unitWord } from "../lib/api";
import { CountUp } from "../components/CountUp";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronDown, Trophy, Video, Award, X, Building2, Medal, Dumbbell, Filter, Wrench } from "lucide-react";

function Shoutouts({ shoutouts, friday }) {
  const key = `sw_shoutout_dismissed_${friday}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(key) === "1");
  if (dismissed || !friday || !shoutouts || shoutouts.length === 0) return null;
  return (
    <div className="mt-8 rounded-lg bg-white/[0.06] border-l-4 border-[#c8102e] px-4 py-4 sm:px-5" data-testid="shoutouts-strip">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-200 flex items-center gap-1.5 min-w-0">
          <Trophy className="w-3.5 h-3.5 text-[#ff4d64] shrink-0" />
          <span className="truncate">Weekly Shoutout — Friday, {new Date(friday + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric" })}</span>
        </div>
        <button onClick={() => { localStorage.setItem(key, "1"); setDismissed(true); }} className="p-2.5 -m-2 text-slate-400 hover:text-white transition-colors shrink-0" aria-label="Dismiss shoutouts" data-testid="shoutouts-dismiss-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex gap-6 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible">
        {shoutouts.map((s) => (
          <div key={s.division} className="shrink-0 sm:shrink min-w-48" data-testid={`shoutout-card-${s.division}`}>
            <div className="text-xs text-slate-400">{s.division} division leader{s.tier ? ` · ${s.tier}` : ""}</div>
            <div className="font-semibold text-sm text-white truncate">{s.name}</div>
            <div className="text-sm font-display font-bold text-[#ff4d64]">{s.task_per_capacity.toFixed(3)} <span className="font-normal text-slate-400 text-xs">pts per {unitWord(s.capacity_type)}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Entry({ e }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`sw-card sw-card-hover rounded-lg ${e.winner ? "bg-[#f3f4f9]" : "bg-white"}`} data-testid={`lb-entry-${e.property_id}`}>
      <button className="w-full flex items-center gap-3 px-4 py-3.5 text-left min-h-14" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`lb-entry-toggle-${e.property_id}`}>
        <span className={`w-8 h-8 rounded-md flex items-center justify-center font-display font-bold text-sm shrink-0 ${e.rank === 1 ? "sw-red text-white" : "bg-slate-100 text-slate-600"}`}>{e.rank}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#1a1f36] flex items-center gap-2 truncate">
            <span className="truncate">{e.name}</span>
            {e.winner && <Trophy className="w-4 h-4 text-[#c8102e] shrink-0" title="Golden Plunger leader" />}
            {e.ironman && <Dumbbell className="w-4 h-4 text-slate-500 shrink-0" title="Lazy Ironman Finisher (+25)" data-testid={`ironman-badge-${e.property_id}`} />}
          </div>
          <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
            <span className="truncate">{e.location}</span>
            {e.visited_by_floors?.length > 0 && (
              <span className="shrink-0 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-semibold" title="Support Corps floors that visited this property" data-testid={`visited-floors-chip-${e.property_id}`}>
                Visited by Fl {e.visited_by_floors.join(" · ")}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-bold text-lg text-[#c8102e]"><CountUp value={e.final_score ?? 0} decimals={1} /></div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Final score</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm border-t border-slate-100 pt-3" data-testid={`lb-entry-detail-${e.property_id}`}>
          <div><div className="text-[11px] uppercase text-slate-400">Task pts</div><div className="font-semibold tnum">{e.raw_task_points}{e.commander_bonus && <span className="text-green-600 text-xs ml-1">+CB</span>}{e.support_visit_bonus > 0 && <span className="text-blue-600 text-xs ml-1">+{e.support_visit_bonus}SV</span>}</div></div>
          <div><div className="text-[11px] uppercase text-slate-400">Per {unitWord(e.capacity_type)}</div><div className="font-semibold tnum">{e.task_per_capacity?.toFixed(3)}</div></div>
          <div><div className="text-[11px] uppercase text-slate-400">Task index</div><div className="font-semibold tnum">{e.task_index?.toFixed(1)}</div></div>
          <div><div className="text-[11px] uppercase text-slate-400">Reel</div><div className="font-semibold tnum">{e.reel_avg != null ? `${e.reel_avg}/30` : "—"}{e.peoples_choice_votes > 0 && <span className="text-xs text-slate-500 ml-1">· {e.peoples_choice_votes} votes</span>}</div></div>
        </div>
      )}
    </div>
  );
}

function Bracket({ b }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? b.entries : b.entries.slice(0, 3);
  const hidden = b.entries.length - 3;
  return (
    <div data-testid={`bracket-${b.division}-${b.tier}`}>
      <div className="sticky top-16 z-10 md:static md:z-auto bg-white/95 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none -mx-4 px-4 py-2 md:mx-0 md:px-0 md:py-0 flex items-baseline justify-between mb-2 md:mb-3">
        <h2 className="font-display text-xl font-semibold text-[#1a1f36]">{b.label}</h2>
        <span className="text-xs text-slate-400" data-testid={`bracket-count-${b.division}-${b.tier}`}>{b.on_board} of {b.enrolled_total} on the board</span>
      </div>
      <div className="space-y-2">
        {b.entries.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-6 text-center">
            <Wrench className="w-6 h-6 text-slate-300 mx-auto mb-2" />
            No one's on the board yet — the first task puts you here.
            <div className="mt-3">
              <Link to="/dashboard" className="inline-flex items-center justify-center min-h-11 px-4 rounded-md border border-[#c8102e]/30 text-[#c8102e] text-sm font-semibold hover:bg-red-50 transition-colors" data-testid={`lb-empty-cta-${b.division}-${b.tier}`}>Log your first task</Link>
            </div>
          </div>
        )}
        {visible.map((e) => <Entry key={e.property_id} e={e} />)}
      </div>
      {hidden > 0 && (
        <button onClick={() => setShowAll(!showAll)} className="w-full mt-2 min-h-11 text-sm font-medium text-slate-500 hover:text-[#1a1f36] rounded-md hover:bg-slate-50 transition-colors flex items-center justify-center gap-1" data-testid={`lb-full-bracket-toggle-${b.division}-${b.tier}`}>
        {showAll ? <>Show top 3 <ChevronDown className="w-4 h-4 rotate-180 transition-transform" /></> : <>Full bracket ({b.entries.length}) <ChevronDown className="w-4 h-4 transition-transform" /></>}
        </button>
      )}
    </div>
  );
}

function SupportCorps({ entries }) {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="mt-16" data-testid="support-corps-section">
      <h2 className="font-display text-xl font-semibold text-[#1a1f36] mb-1 flex items-center gap-2"><Building2 className="w-5 h-5 text-slate-400" />Support Corps — floor vs floor</h2>
      <p className="text-sm text-slate-500 mb-4">Home-office floors earn <b>+15 per approved property visit</b> (on-site or by Teams). Most visit points wins — every visit also hands the host property +10 (max 3 per property).</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {entries.map((f) => (
          <div key={f.property_id} className={`sw-card rounded-lg p-4 ${f.winner ? "bg-[#f3f4f9]" : "bg-white"}`} data-testid={`support-floor-${f.property_id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-7 h-7 rounded-md flex items-center justify-center font-display font-bold text-sm ${f.rank === 1 ? "sw-red text-white" : "bg-slate-100 text-slate-600"}`}>{f.rank}</span>
              <div className="font-semibold text-[#1a1f36] truncate">{f.name}</div>
              {f.winner && <Trophy className="w-4 h-4 text-[#c8102e] shrink-0" />}
              {f.ironman && <Dumbbell className="w-4 h-4 text-slate-500 shrink-0" title="Lazy Ironman Finisher (+25)" data-testid={`ironman-badge-${f.property_id}`} />}
            </div>
            <div className="font-display font-bold text-2xl text-[#c8102e]" data-testid={`support-floor-points-${f.property_id}`}><CountUp value={f.total_points} /></div>
            <div className="text-xs text-slate-500">visit points · {f.visits} approved visit{f.visits === 1 ? "" : "s"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Regionals({ regionals }) {
  if (!regionals) return null;
  const medal = (i) => ["text-[#c8102e]", "text-slate-400", "text-slate-300"][i] || "text-slate-200";
  const list = (items, render, testid) => (
    <div className="divide-y divide-slate-100" data-testid={testid}>
      {items.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg p-4 text-center">Standings appear once tasks start rolling in.</p>}
      {items.map((r, i) => (
        <div key={r.regional} className="py-3 flex items-center gap-3">
          <Medal className={`w-5 h-5 shrink-0 ${medal(i)}`} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-[#1a1f36] truncate">{r.regional}</div>
            <div className="text-xs text-slate-500">{r.properties} propert{r.properties === 1 ? "y" : "ies"} in portfolio</div>
          </div>
          <div className="text-right shrink-0">{render(r)}</div>
        </div>
      ))}
    </div>
  );
  return (
    <div className="mt-16" data-testid="regionals-section">
      <h2 className="font-display text-xl font-semibold text-[#1a1f36] mb-1 flex items-center gap-2"><Award className="w-5 h-5 text-slate-400" />Regional Competition</h2>
      <p className="text-sm text-slate-500 mb-4">Commanders compete too — live standings across each Regional's portfolio.</p>
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
        <div>
          <h3 className="font-display font-semibold text-[#1a1f36]">Best Commander</h3>
          <p className="text-xs text-slate-400 mb-1">Highest average points per bed/unit across their properties</p>
          {list(regionals.best_commander, (r) => (
            <><div className="font-display font-bold text-lg text-[#c8102e]">{r.avg_task_per_capacity.toFixed(3)}</div><div className="text-[11px] uppercase text-slate-400">avg pts / capacity</div></>
          ), "best-commander-list")}
        </div>
        <div>
          <h3 className="font-display font-semibold text-[#1a1f36]">Most Engaged Portfolio</h3>
          <p className="text-xs text-slate-400 mb-1">Highest share of their properties on the board (≥1 task logged)</p>
          {list(regionals.most_engaged, (r) => (
            <><div className="font-display font-bold text-lg text-[#c8102e]">{r.engaged_pct}%</div><div className="text-[11px] uppercase text-slate-400">{r.on_board} of {r.properties} on board</div></>
          ), "most-engaged-list")}
        </div>
      </div>
    </div>
  );
}

function RegionalFilterBar({ regionNames, region, setRegion, filtering }) {
  if (regionNames.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-8" data-testid="lb-regional-filter-bar">
      <div className="text-sm text-slate-500 flex items-center gap-2">
        {filtering ? (
          <>
            <span data-testid="lb-regional-filter-active">Showing <b className="text-[#1a1f36]">{region}</b>'s portfolio — ranks stay bracket-wide.</span>
            <button onClick={() => setRegion("all")} className="min-h-11 px-2 text-[#c8102e] font-medium hover:underline flex items-center gap-0.5" data-testid="lb-regional-clear"><X className="w-3.5 h-3.5" />Clear</button>
          </>
        ) : (
          <span className="flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" />Filter the brackets by Regional</span>
        )}
      </div>
      <div className="w-full sm:w-64">
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger data-testid="lb-regional-filter"><SelectValue placeholder="All regionals" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="lb-regional-option-all">All regionals</SelectItem>
            {regionNames.map((r) => <SelectItem key={r} value={r} data-testid={`lb-regional-option-${r}`}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function dayChip(cfg) {
  if (!cfg) return null;
  const diff = (a, b) => Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000);
  const total = diff(cfg.campaign_start, cfg.campaign_end) + 1;
  if (cfg.today < cfg.campaign_start) return { big: `${diff(cfg.today, cfg.campaign_start)}d`, label: "to kickoff" };
  if (cfg.today > cfg.campaign_end) return { big: "Done", label: "competition wrapped" };
  return { big: `Day ${diff(cfg.campaign_start, cfg.today) + 1}`, label: `of ${total}` };
}

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [region, setRegion] = useState("all");

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setData(data));
    api.get("/prizes").then(({ data }) => setPrizes(data));
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/regionals/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div data-testid="lb-skeleton">
        <section className="sw-navy">
          <div className="max-w-7xl mx-auto px-4 py-14 space-y-4">
            <Skeleton className="h-4 w-56 bg-white/10" />
            <Skeleton className="h-12 w-80 max-w-full bg-white/10" />
            <Skeleton className="h-4 w-64 bg-white/10" />
          </div>
        </section>
        <div className="max-w-7xl mx-auto px-4 py-10 grid lg:grid-cols-2 gap-8">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-6 w-44" />
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  const lb = data.data;

  const regionNames = [...new Set([...lb.brackets.flatMap((b) => b.entries), ...lb.needs_capacity].map((e) => e.regional).filter(Boolean))].sort();
  const filtering = region !== "all";
  const filterEntries = (entries) => (filtering ? entries.filter((e) => e.regional === region) : entries);
  const brackets = lb.brackets.map((b) => ({ ...b, entries: filterEntries(b.entries) })).filter((b) => !filtering || b.entries.length > 0);
  const needsCapacity = filterEntries(lb.needs_capacity);

  const day = dayChip(config);
  const teamsOnBoard = lb.brackets.reduce((n, b) => n + b.on_board, 0) + lb.support.filter((f) => f.total_points > 0).length;
  const tasksApproved = summary == null ? null : summary.reduce((n, r) => n + r.approved_subs, 0) + lb.support.reduce((n, f) => n + f.visits, 0);
  const chips = [
    day && { id: "hero-chip-day", big: day.big, label: day.label },
    { id: "hero-chip-onboard", big: <CountUp value={teamsOnBoard} />, label: "teams on the board" },
    tasksApproved != null && { id: "hero-chip-approved", big: <CountUp value={tasksApproved} />, label: "tasks approved" },
  ].filter(Boolean);

  return (
    <div>
      <section className="sw-navy sw-diag text-white">
        <div className="max-w-7xl mx-auto px-4 py-10 sm:py-14 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:items-center">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#ff4d64] mb-3">Redstone Residential · July 2026</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">Site Wars Leaderboard</h1>
              <p className="mt-3 text-slate-300">Six brackets. One month. Scored per bed. <Link to="/how-it-works" className="text-white font-medium underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors" data-testid="hero-rules-link">Full rules →</Link></p>
              <p className="mt-4 text-xs text-slate-400" data-testid="last-updated-stamp">Updated Fridays{data.published_at ? ` · Last published ${new Date(data.published_at).toLocaleString()}` : ""}</p>
            </div>
            <div className="flex flex-wrap lg:justify-end gap-3 stagger">
              {chips.map((c) => (
                <div key={c.id} className="rounded-lg bg-white/[0.07] px-5 py-3.5 min-w-[8.5rem]" data-testid={c.id}>
                  <div className="font-display font-bold text-2xl sm:text-3xl leading-tight">{c.big}</div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mt-0.5">{c.label}</div>
                </div>
              ))}
            </div>
          </div>
          <Shoutouts shoutouts={data.shoutouts} friday={data.shoutout_friday} />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <RegionalFilterBar regionNames={regionNames} region={region} setRegion={setRegion} filtering={filtering} />
        {filtering && brackets.length === 0 && needsCapacity.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-8 text-center mb-8" data-testid="lb-regional-empty">
            None of {region}'s properties are on the board yet — the first approved task puts them here.
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-x-10 gap-y-10 stagger">
          {brackets.map((b) => <Bracket key={b.label} b={b} />)}
        </div>

        {!filtering && <SupportCorps entries={lb.support} />}

        {!filtering && <Regionals regionals={lb.regionals} />}

        {needsCapacity.length > 0 && (
          <div className="mt-16" data-testid="needs-capacity-section">
            <h2 className="font-display text-xl font-semibold text-[#1a1f36] mb-1">Waiting on capacity</h2>
            <p className="text-sm text-slate-500 mb-3">These properties must set capacity to compete — they're in, but not ranked yet.</p>
            <div className="flex flex-wrap gap-2">
              {needsCapacity.map((e) => (
                <span key={e.property_id} className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-md text-xs font-medium">{e.name}</span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16">
          <h2 className="font-display text-xl font-semibold text-[#1a1f36] mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-slate-400" />What's on the line</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
            {prizes.map((p) => (
              <div key={p.name} className="sw-card sw-card-hover bg-white rounded-lg p-4">
                <div className="font-semibold text-[#1a1f36] text-sm">{p.name}</div>
                <div className="text-xs text-slate-500 mt-1">{p.detail}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 bg-[#f8f9fc] rounded-lg p-5 flex items-center gap-4">
          <Video className="w-8 h-8 text-[#c8102e] shrink-0" />
          <div>
            <div className="font-semibold text-[#1a1f36]">People's Choice</div>
            <div className="text-sm text-slate-600">Watch every property's reel and vote for your favorite. It's bragging rights only — votes never change the score. <a href="/reels" className="text-[#c8102e] font-medium hover:underline">Watch the reels →</a></div>
          </div>
        </div>
      </div>
    </div>
  );
}
